// Minimal hash router: no framework, just enough to switch views and pass
// simple params (#/clients/:id style).

type ViewFn = (root: HTMLElement, params: Record<string, string>) => void | Promise<void>;

interface Route {
  pattern: RegExp;
  keys: string[];
  view: ViewFn;
}

const routes: Route[] = [];

export function register(path: string, view: ViewFn) {
  const keys: string[] = [];
  const pattern = new RegExp(
    "^" +
      path.replace(/:([\w]+)/g, (_m, key) => {
        keys.push(key);
        return "([^/]+)";
      }) +
      "$",
  );
  routes.push({ pattern, keys, view });
}

export function navigate(path: string) {
  window.location.hash = path;
}

/** Message block built from text nodes: nothing from the URL is ever parsed as HTML. */
function messageBlock(text: string): HTMLElement {
  const el = document.createElement("div");
  el.className = "empty";
  el.textContent = text.length > 200 ? `${text.slice(0, 200)}…` : text;
  return el;
}

/** Percent-decodes route params; null when the address is malformed (for example "#/clients/%"). */
function decodeParams(keys: string[], match: RegExpMatchArray): Record<string, string> | null {
  const params: Record<string, string> = Object.create(null);
  try {
    keys.forEach((key, i) => (params[key] = decodeURIComponent(match[i + 1])));
  } catch {
    return null;
  }
  return params;
}

export function startRouter(root: HTMLElement) {
  // Each render gets a token so a view that fails (or finishes) after the person navigated away cannot paint an error into the next page.
  let renderToken = 0;
  const render = () => {
    const token = ++renderToken;
    const hash = window.location.hash.replace(/^#/, "") || "/";
    const routePath = hash.split(/[?#]/, 1)[0] || "/";
    for (const route of routes) {
      const match = routePath.match(route.pattern);
      if (match) {
        const params = decodeParams(route.keys, match);
        root.replaceChildren();
        window.scrollTo(0, 0);
        highlightNav(hash);
        if (!params) {
          root.append(messageBlock(`This address isn't valid: ${hash}`));
          return;
        }
        const fail = (error: unknown) => {
          if (token !== renderToken) return;
          console.error("View failed", error);
          root.replaceChildren(messageBlock("This page couldn't be displayed. Try again, or go back."));
        };
        try {
          void Promise.resolve(route.view(root, params)).catch(fail);
        } catch (error) {
          fail(error);
        }
        return;
      }
    }
    root.replaceChildren(messageBlock(`Not found: ${hash}`));
  };
  window.addEventListener("hashchange", render);
  render();
}

function highlightNav(hash: string) {
  const path = hash.split(/[?#]/, 1)[0] || "/";
  let currentLabel = "Client workspace";
  document.querySelectorAll<HTMLAnchorElement>(".nav a[data-path]").forEach((a) => {
    const base = a.dataset.path ?? "";
    const active = base === "/" ? path === "/" || path === "/overview" : path === base || path.startsWith(`${base}/`);
    a.classList.toggle("active", active);
    if (active) {
      a.setAttribute("aria-current", "page");
      if (!path.startsWith("/clients/") || base !== "/clients") currentLabel = a.querySelector("span")?.textContent?.trim() || currentLabel;
    } else a.removeAttribute("aria-current");
  });
  const context = document.getElementById("app-context");
  if (context) context.textContent = path.startsWith("/clients/") ? "Client workspace" : currentLabel;
}
